import moment, { Moment } from "moment";
import { CreatePaymentPlanRequest } from "../Dto/CreatePaymentPlanRequest";
import { ParametricConstraintMessage } from "pages/components/Form/FormEntities";
import { PaymentIn } from "../Entity/PaymentPlan";

const securedLastPayout = 0.1;
const PAYMENT_IN_MONTH_DATE = 5;
const PAYMENT_OUT_MONTH_DATE = 15;

export type Collection = Array<{
  amount: number;
  date: Moment | ParametricConstraintMessage;
}>;



function getSecuredPayouts(
  paymentPlanRequest: CreatePaymentPlanRequest,
): Collection {
  const financedAmount = paymentPlanRequest.financedAmount;
  const numberOfSessions = paymentPlanRequest.numberOfSessions;
  const numberOfSequences = paymentPlanRequest.numberOfSequences;
  const lastPayment = Math.floor(financedAmount * securedLastPayout);

  const regularAmount = Math.floor(
    (financedAmount - lastPayment) / numberOfSessions
  );
  const firstAmount =
    financedAmount - lastPayment - regularAmount * (numberOfSessions - 1);

  const paymentsOffSet =
    numberOfSessions === 1 &&
    [10, 12].includes(paymentPlanRequest.numberOfSequences)
      ? 3
      : 2;

  const collections = [
    {
      amount: Math.floor(
        firstAmount - getFeesValue(paymentPlanRequest)
      ),
      date: moment().add(paymentsOffSet, "month").date(PAYMENT_OUT_MONTH_DATE),
    },
  ] as Collection;

  for (var i = 2; i <= numberOfSessions; i++) {
    collections.push({
      amount: regularAmount,
      date: {
        name: "CONTENT.after_the_session",
        params: {
          session: `${i}`,
        },
      },
    });
  }

  collections.push({
    amount: lastPayment,
    date: moment()
      .add(numberOfSequences - 1, "month")
      .date(PAYMENT_OUT_MONTH_DATE),
  });

  return collections;
}

function getFeesValue(paymentPlanRequest: CreatePaymentPlanRequest){
  return (paymentPlanRequest.financedAmount * getFees(paymentPlanRequest)) / 100;
}

function getVATValue(paymentPlanRequest: CreatePaymentPlanRequest){
  return (getFeesValue(paymentPlanRequest) * getVAT(paymentPlanRequest)) / 100;
}

function getManagementPayouts(paymentPlanRequest: CreatePaymentPlanRequest): Collection {
  const financedAmount = paymentPlanRequest.financedAmount;
  const initialAmount = paymentPlanRequest.initialAmount;
  const numberOfSequences = paymentPlanRequest.numberOfSequences;

  const regularAmount = Math.floor(
    (financedAmount - initialAmount) / (numberOfSequences - 1)
  );

  const firstAmount = financedAmount - regularAmount * (numberOfSequences - 1);
  const fees = getFeesValue(paymentPlanRequest);
  const vat =  getVATValue(paymentPlanRequest);

  const collections: Collection = [
    {
      amount: Math.floor(
        firstAmount - fees - vat
      ),
      date: moment().add(1, "month").date(PAYMENT_OUT_MONTH_DATE),
    } 
  ] ;
  for (var i = 1; i < numberOfSequences; i++) {
    if (i === 1) {
      collections[0].amount += regularAmount;
    } else {
      collections.push({
        amount: regularAmount,
        date: moment().add(i, "month").date(PAYMENT_OUT_MONTH_DATE),
      });
    }
  }

  return collections;
}

function getFees(paymentPlanRequest: CreatePaymentPlanRequest) {
  if (paymentPlanRequest.secured) {
    if (paymentPlanRequest.numberOfSessions === 1) {
      return paymentPlanRequest.numberOfSequences + 4;
    } else {
      return 12;
    }
  }
  return 4 + paymentPlanRequest.numberOfSequences / 4;
}


function getVAT(paymentPlanRequest: CreatePaymentPlanRequest) {
    return paymentPlanRequest.secured
      ? 0
      : 20;
}

export class PaymentPlanConfig {
  paymentPlanRequest: CreatePaymentPlanRequest;

  constructor(paymentPlanRequest: CreatePaymentPlanRequest) {
    this.paymentPlanRequest = paymentPlanRequest;
  }

  getPayInsCollections() {
      const financedAmount = this.paymentPlanRequest.financedAmount;
      const initialAmount = this.paymentPlanRequest.initialAmount;
      const numberOfSequences = this.paymentPlanRequest.numberOfSequences;

      const regularAmount = Math.floor(
        (financedAmount - initialAmount) / (numberOfSequences - 1)
      );

      const firstAmount = financedAmount - regularAmount * (numberOfSequences - 1);
      const collections: Collection = [
        {
          amount: firstAmount,
          date: moment(),
        },
      ];
      for (var i = 1; i < numberOfSequences; i++) {
        collections.push({
          amount: regularAmount,
          date: moment().add(i, "month").date(PAYMENT_IN_MONTH_DATE),
        });
      }

      return collections;
  }
  getPayOutsCollections() {
    if (this.paymentPlanRequest.secured) {
      return getSecuredPayouts(this.paymentPlanRequest);
    }
    return getManagementPayouts(this.paymentPlanRequest);
  }

  getFees() {
    return getFees(this.paymentPlanRequest);
  }

  getVAT() {
    return getVAT(this.paymentPlanRequest);
  }

  getFeesValue() {
    return getFeesValue(this.paymentPlanRequest);
  }

  getVATValue() {
    return getVATValue(this.paymentPlanRequest);
  }

  getSecurityChequeData()
  : null | {number: number, amount: number, date: Moment } 
   {
    if (
      this.paymentPlanRequest.secured 
      || !this.paymentPlanRequest.financedAmount 
      || this.paymentPlanRequest.numberOfSequences <= 2
    ) {
      return null;
    }
    const paymentsIn = this.getPayInsCollections();
    const lastPaymentIn = paymentsIn[paymentsIn.length - 1]

    // const lastDate = ((typeof lastPaymentIn.date) === 'Moment') ? lastPaymentIn.date : "";
    const lastDate = (moment.isMoment(lastPaymentIn.date)) ? lastPaymentIn.date : moment(null);
    return {
      number: this.paymentPlanRequest.numberOfSequences - 2,
      // paymentIn: this.getPayInsCollections(),
      amount: lastPaymentIn.amount,
      date: lastDate,
    }
  }

  getInitialAmountRatios() {
    return {
      min:
        this.paymentPlanRequest.secured &&
        this.paymentPlanRequest.numberOfSessions === 1
          ? 0.3
          : 0.1,
      max: 0.5,
    };
  }

  getFinancedAmountRange() {
    return {
      min: 200,
      max: 5000,
    };
  }

  getDefaultNumberOfSequences() {
    const financedAmount = this.paymentPlanRequest.financedAmount;

    let defaultNumberOfSequences;

    if (financedAmount <= 500) {
      defaultNumberOfSequences = 2;
    } else if (financedAmount <= 1000) {
      defaultNumberOfSequences = 4;
    } else if (financedAmount <= 2000) {
      defaultNumberOfSequences = 6;
    } else if (financedAmount <= 3000) {
      defaultNumberOfSequences = 8;
    } else if (financedAmount <= 4000) {
      defaultNumberOfSequences = 10;
    } else {
      defaultNumberOfSequences = 12;
    }
    return defaultNumberOfSequences;
  }
}
